import ChevronDownIcon from '@assets/icons/chevron-down.svg';
import ChevronUpIcon from '@assets/icons/chevron-up.svg';
import i18n from '@/utils/i18n';
import MinusIcon from '@assets/icons/minus.svg';
import PlusIcon from '@assets/icons/plus.svg';
import { AttachmentListControlButton } from './attachment-list-control-button';
import { createStyles } from '@/contexts/theme-context';
import {
  IMAGE_MAP_MAX_ZOOM,
  IMAGE_MAP_MIN_ZOOM,
} from './attachment-list-types';
import { shadow, TYPOGRAPHY } from '@/styles';
import { StyleProp, Text, View, ViewStyle } from 'react-native';

const ZOOM_STEP = 0.25;

interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  hideZoomControls?: boolean;
  imageCount: number;
  imageIndex: number;
  setImageIndex: (nextIndex: number) => void;
  setZoomLevel?: (nextZoomLevel: number) => void;
  zoomLevel?: number;
}

export const AttachmentListControls: React.FC<Props> = ({
  containerStyle,
  hideZoomControls,
  imageCount,
  imageIndex,
  setImageIndex,
  setZoomLevel,
  zoomLevel = 1,
}) => {
  const [styles] = useStyles();

  if (hideZoomControls && imageCount === 1) {
    return null;
  }

  return (
    <View style={[styles.controlsContainer, containerStyle]}>
      {!hideZoomControls ? (
        <>
          <AttachmentListControlButton
            accessibilityLabel={i18n.t('image_map.zoom_out_button')}
            disabled={zoomLevel <= IMAGE_MAP_MIN_ZOOM}
            Icon={MinusIcon}
            iconProps={{
              height: 24,
              width: 24,
            }}
            onPress={() =>
              setZoomLevel?.(
                Math.max(zoomLevel - ZOOM_STEP, IMAGE_MAP_MIN_ZOOM),
              )
            }
            trackingData={{
              name: 'image_map.zoom_out_button',
            }}
          />
          <Text style={[TYPOGRAPHY.small, styles.controlText]}>
            {Math.round(zoomLevel * 100)}%
          </Text>
          <AttachmentListControlButton
            accessibilityLabel={i18n.t('image_map.zoom_in_button')}
            disabled={zoomLevel >= IMAGE_MAP_MAX_ZOOM}
            Icon={PlusIcon}
            iconProps={{
              height: 24,
              width: 24,
            }}
            onPress={() =>
              setZoomLevel?.(
                Math.min(zoomLevel + ZOOM_STEP, IMAGE_MAP_MAX_ZOOM),
              )
            }
            trackingData={{
              name: 'image_map.zoom_in_button',
            }}
          />
          {imageCount > 1 ? <View style={styles.controlDivider} /> : null}
        </>
      ) : null}
      {imageCount > 1 ? (
        <>
          <AttachmentListControlButton
            accessibilityLabel={i18n.t('image_map.previous_page_button')}
            disabled={imageIndex === 0}
            Icon={ChevronUpIcon}
            iconProps={{
              height: 8,
              width: 16,
            }}
            onPress={() => setImageIndex(Math.max(imageIndex - 1, 0))}
            trackingData={{
              name: 'image_map.previous_page_button',
            }}
          />
          <Text style={[TYPOGRAPHY.small, styles.controlText]}>
            {i18n.t('page_of', {
              number: imageIndex + 1,
              total: imageCount,
            })}
          </Text>
          <AttachmentListControlButton
            accessibilityLabel={i18n.t('image_map.next_page_button')}
            disabled={imageIndex === imageCount - 1}
            Icon={ChevronDownIcon}
            iconProps={{
              height: 8,
              width: 16,
            }}
            onPress={() =>
              setImageIndex(Math.min(imageIndex + 1, imageCount - 1))
            }
            trackingData={{
              name: 'image_map.next_page_button',
            }}
          />
        </>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  controlDivider: {
    alignSelf: 'stretch',
    backgroundColor: theme.sk.color.border.primary,
    width: 1,
  },
  controlsContainer: {
    ...shadow({
      color: theme.colors.black,
      elevation: 0,
      opacity: 0.05,
      radius: 12,
      x: 0,
      y: 6,
    }),
    alignItems: 'center',
    alignSelf: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: 32,
    bottom: 16,
    flexDirection: 'row',
    gap: 4,
    height: 32,
    justifySelf: 'anchor-center',
    paddingHorizontal: 4,
    position: 'absolute',
  },
  controlText: {
    color: theme.sk.color.text.primary,
    minWidth: 40,
    textAlign: 'center',
  },
}));
